import {
  EnrollmentReviewMode,
  EnrollmentWithCases,
  fetchEnrollmentWithChildCases,
  hideLoader,
  HcpEnrollmentDetailMultiproduct,
  showLoader,
  useTenantNavigate,
  ENROLLMENT_REVIEW_MODE,
} from '@app-store'
import { useCallback, useEffect, useState } from 'react'
import { RootState, useAppDispatch, useAppSelector } from '../utils/agent.store'
import { QUEUE_STRINGS } from '../utils/queueIntake.types'
import useWatchedRef from './useWatchedRef'
import { updateEnrollmentStatus } from '../pages/intake-ques/multi-product/loadCaseWorkflow'
import { ENROLLMENT_STATUS } from '../utils/constants'
import { useLocation, useParams } from 'react-router-dom'

export interface EnrollmentContextValue {
  enrollment: EnrollmentWithCases | null
  childCases: HcpEnrollmentDetailMultiproduct[]
  reviewMode: EnrollmentReviewMode
  enrollmentStatus?: string
  isLoading: boolean
  isMultiProductMode: boolean
  refetch: () => Promise<{
    cases: HcpEnrollmentDetailMultiproduct[]
    has_dedupe_resolved: boolean | undefined
  }>
  setReviewMode: (mode: EnrollmentReviewMode) => void
}

export const useEnrollmentContext = (
  communicationId: string | undefined,
  entityName: string | undefined,
  enabled: boolean
): EnrollmentContextValue => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { entityid: entityId, layoutid, id } = useParams()
  const navigate = useTenantNavigate()
  const [enrollment, setEnrollment] = useState<EnrollmentWithCases | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [reviewMode, setReviewMode] = useWatchedRef<EnrollmentReviewMode>(
    ENROLLMENT_REVIEW_MODE.LEGACY
  )
  const [childCases, setChildCases] = useState<
    HcpEnrollmentDetailMultiproduct[]
  >([])
  const siteSettings = useAppSelector(
    (state: RootState) => state.authUser.site_settings
  )
  const hasMultiProductSetting = siteSettings?.find(
    (item) => item.key === QUEUE_STRINGS.IS_MULTI_PRODUCT_ENABLED
  )?.value

  const hasMultiProduct =
    hasMultiProductSetting === true || hasMultiProductSetting === 'true'

  const isMultiProductMode =
    ((location?.state?.hasMultiProduct ??
      sessionStorage.getItem(QUEUE_STRINGS.HAS_MULTI_PRODUCT) ===
        QUEUE_STRINGS.TRUE_STRING) &&
      entityName === QUEUE_STRINGS?.HCP_PORTAL_ENTITY_LOWERCASE) ||
    (hasMultiProduct &&
      entityName === QUEUE_STRINGS?.HCP_PORTAL_ENTITY_LOWERCASE)

  const fetchEnrollment = useCallback(async () => {
    if ((!enabled || !communicationId) && !isMultiProductMode) {
      setEnrollment(null)
      setChildCases([])
      return {
        cases: [] as HcpEnrollmentDetailMultiproduct[],
        has_dedupe_resolved: undefined,
      }
    }

    setIsLoading(true)
    dispatch(showLoader())
    try {
      const response = await fetchEnrollmentWithChildCases(
        parseInt(communicationId ?? '', 10)
      )
      const cases = response?.data?.hcp_enrollment_details ?? []

      const allRemainingCases = cases.filter(
        (item) => !item?.workflow_proceeded
      )
      if (allRemainingCases.length === 0) {
        await updateEnrollmentStatus(
          communicationId ?? '',
          ENROLLMENT_STATUS.COMPLETED,
          true
        )

        navigate(`/manage-queue/dynamic-queue/${entityId}/${layoutid}/${id}`)
        return {
          cases: [] as HcpEnrollmentDetailMultiproduct[],
          has_dedupe_resolved: undefined,
        }
      }
      setEnrollment(response?.data ?? null)
      setChildCases(cases)

      // Set initial multi-product mode once; navigation helper owns later switches.
      if (
        isMultiProductMode &&
        cases.length > 0 &&
        response?.data?.has_dedup_resolved === false
      ) {
        setReviewMode(ENROLLMENT_REVIEW_MODE.DATA_REVIEW)
      } else if (isMultiProductMode && response?.data?.has_dedup_resolved) {
        setReviewMode(ENROLLMENT_REVIEW_MODE.INTAKE_REVIEW)
      }

      return {
        cases,
        has_dedupe_resolved: response?.data?.has_dedup_resolved,
      }
    } catch (err) {
      console.error(err)
      setEnrollment(null)
      setChildCases([])
      return {
        cases: [] as HcpEnrollmentDetailMultiproduct[],
        has_dedupe_resolved: undefined,
      }
    } finally {
      setIsLoading(false)
      dispatch(hideLoader())
    }
  }, [
    communicationId,
    dispatch,
    enabled,
    entityName,
    entityId,
    layoutid,
    id,
    navigate,
    isMultiProductMode,
    setReviewMode,
  ])
  const enrollmentStatus = enrollment?.enrollment_status?.value

  useEffect(() => {
    if (isMultiProductMode) {
      fetchEnrollment()
    }
  }, [fetchEnrollment, isMultiProductMode])

  return {
    enrollment,
    childCases,
    reviewMode: reviewMode.current ?? ENROLLMENT_REVIEW_MODE.LEGACY,
    enrollmentStatus,
    isLoading,
    isMultiProductMode: isMultiProductMode ?? false,
    refetch: fetchEnrollment,
    setReviewMode,
  }
}
